import { formatDate } from "utils/Common/Date";
import { capitalizeFirst } from "utils/Common/String";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import { useDocuments } from "hooks/useDocuments";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import EmptyPreconReportIcon from "Assets/Img/EmptyPreconReportIcon";
import Button from "Components/Common/Button/Button";

interface PreconReviewData {
  reportDate: string;
  project: {
    name: string;
    type: string;
    ownerCompany: string;
    gcCompany: string;
  };
  hardCostBudget: {
    amount: number;
    currency: { code: string; symbol: string; name: string };
  };
  estCompletionDate: string;
}

interface Props {
  data: PreconReviewData | null;
  loading?: boolean;
  notFound?: boolean;
  projectId?: string;
  preconReportId?: string;
}

export default function PreconSection({
  data,
  loading,
  notFound,
  projectId,
  preconReportId,
}: Props) {
  const { handleDownload, downloadingId } = useDocuments(projectId, false);

  const handleDownloadPdf = () => {
    if (!preconReportId) return;
    handleDownload(
      {
        id: preconReportId,
        label: data?.project?.name
          ? `PreconReport-${data.project.name}.pdf`
          : "precon-report.pdf",
      },
      true,
    );
  };
  const symbol = data?.hardCostBudget?.currency?.symbol ?? "";
  const amount = data?.hardCostBudget?.amount;
  const formattedBudget =
    symbol && amount != null
      ? `${symbol}${amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
      : "—";

  if (notFound) {
    return (
      <div className="ds active" id="ds-precon">
        <EmptyState
          icon={<EmptyPreconReportIcon />}
          title="Pre-Construction Report Not Available"
          subtitle="The admin has not yet submitted the pre-construction report for this project. Check back once it has been completed and published."
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="ds active" id="ds-precon">
        <Skeleton width={200} height={18} style={{ marginBottom: 16 }} />
        <Skeleton width="100%" height={36} style={{ marginBottom: 16 }} />
        <div className="card">
          <Skeleton width="60%" height={16} style={{ marginBottom: 14 }} />
          <div className="precon-kv-grid">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <Skeleton key={n} width="80%" height={13} />
            ))}
          </div>
          <Skeleton width="100%" height={52} style={{ marginBottom: 10 }} />
          <Skeleton width="100%" height={44} style={{ marginBottom: 10 }} />
          <Skeleton width="100%" height={36} />
        </div>
      </div>
    );
  }

  return (
    <div className="ds active" id="ds-precon">
      <div className="dsHeaderTitle">Pre-Construction Report</div>
      <div className="ro-notice">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="6" stroke="var(--tx3)" strokeWidth="1.2" />
          <line
            x1="7"
            y1="5.5"
            x2="7"
            y2="9"
            stroke="var(--tx3)"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
          <circle cx="7" cy="4" r=".6" fill="var(--tx3)" />
        </svg>
        Pre-construction report prepared by CSC New England. Read-only.
      </div>
      <div className="card">
        <div className="card-title">
          Pre-Construction Review —{" "}
          {data?.reportDate ? formatDate(data.reportDate, false) : ""}
          <Button
            className="btn btn-sm"
            onClick={handleDownloadPdf}
            disabled={downloadingId === preconReportId || !preconReportId}
          >
            {downloadingId === preconReportId ? (
              <>
                <span className="btn-spinner" /> Downloading…
              </>
            ) : (
              "Download PDF"
            )}
          </Button>
        </div>
        <div className="precon-kv-grid">
          <div>
            <span className="precon-kv-label">Project:</span>{" "}
            <strong>{data?.project?.name ?? "—"}</strong>
          </div>
          <div>
            <span className="precon-kv-label">Type:</span>{" "}
            {capitalizeFirst(data?.project?.type ?? "") ?? "—"}
          </div>
          <div>
            <span className="precon-kv-label">Owner:</span>{" "}
            {data?.project?.ownerCompany ?? "—"}
          </div>
          <div>
            <span className="precon-kv-label">Contractor:</span>{" "}
            {data?.project?.gcCompany ?? "—"}
          </div>
          <div>
            <span className="precon-kv-label">Hard cost budget:</span>{" "}
            <strong>{formattedBudget}</strong>
          </div>
          <div>
            <span className="precon-kv-label">Est. completion:</span>{" "}
            {data?.estCompletionDate
              ? formatDate(data.estCompletionDate, false)
              : "—"}
          </div>
        </div>
        <div className="precon-analysis">
          Budget falls within typical range for {data?.project?.type + " "}
          construction. CSC reviewed plans, specifications, and Schedule of
          Values. All major trades represented for a complete project.
        </div>
        <div className="precon-conditions">
          <strong>Bank Conditions:</strong> Verify equity before each draw.
          Title update required. All insurance certificates must remain current.
        </div>
        <div className="precon-recommendation">
          <strong>CSC Recommendation:</strong> Project approved to proceed.
          First draw authorized upon receipt of building permit.
        </div>
      </div>
    </div>
  );
}
